import ApiService from '@/common/api.service'
import JwtService from '@/common/jwt.service'
import MemberClientApi from '@/api/member/member-client.js'
import { LOGIN, LOGOUT, CHECK_AUTH, LOGIN_CLIENT, LOGOUT_CLIENT, CHECK_AUTH_CLIENT, SET_OPEN_LOGIN_DIALOG, SET_OPEN_REGISTER_DIALOG } from '../actions.type'
import { SET_AUTH, SET_ERROR, PURGE_AUTH, SET_AUTH_CLIENT, SET_UPDATE_AUTH_CLIENT, SET_ERROR_CLIENT, PURGE_AUTH_CLIENT, OPEN_LOGIN_DIALOG, OPEN_REGISTER_DIALOG, SET_LOGIN_DIALOG_CONTEXT, SET_PASSWORD_REMIND, SET_ROLE_PERMISSION_GROUP, PURGE_ROLE_PERMISSION_GROUP } from '../mutations.type'

// The backend's post-login UMS profile sync runs asynchronously after POST /member/login
// responds, so the selfInfo fetch that immediately follows login can miss it. This fire-and-
// forget re-fetch, delayed long enough for that sync to realistically finish, silently
// refreshes the header's `currentUser` display if it changed. Never blocks the login flow;
// a failure here is swallowed.
const UMS_SYNC_PROFILE_REFRESH_DELAY_MS = 2500

function refreshProfileAfterUmsSync (context) {
  setTimeout(() => {
    ApiService.get('/member/selfInfo')
      .then(response => {
        context.commit(SET_UPDATE_AUTH_CLIENT, response.data)
      })
      .catch(() => {})
  }, UMS_SYNC_PROFILE_REFRESH_DELAY_MS)
}

const state = {
  user: {},
  errors: null,
  isAuthenticated: !!JwtService.getToken(),
  isOpenLoginDialog: false,
  loginDialogContext: null,
  isOpenRegisterDialog: false,
  isPasswordReminded: false,
  rolePermissionGroup: {}
}

const getters = {
  currentUser (state) {
    return state.user
  },
  isAuthenticated (state) {
    return state.isAuthenticated
  },
  isOpenLoginDialog (state) {
    return state.isOpenLoginDialog
  },
  loginDialogContext (state) {
    return state.loginDialogContext
  },
  isOpenRegisterDialog (state) {
      return state.isOpenRegisterDialog
  },
  isPasswordReminded (state) {
    return state.isPasswordReminded
  },
  rolePermissionGroup (state) {
      return state.rolePermissionGroup
  }
}

const actions = {
  [LOGIN] (context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post('/admin/login', credentials)
        .then(response => {
          const apiToken = response.headers['api-token']
          JwtService.saveToken(apiToken)
          // リフレッシュトークンの取得と保存
          const refreshToken = response.headers['api-refresh-token']
          JwtService.saveRefreshToken(refreshToken)
          ApiService.setHeader()
          context.commit(SET_PASSWORD_REMIND, response.data.passwordRemind)
          resolve(response.data.id)
        })
        .catch(err => {
          context.commit(SET_ERROR, err)
          JwtService.destroyAll()
          reject(err)
        })
    })
      .then(() => {
        return new Promise((resolve, reject) => {
          ApiService.get('/admin/refer')
            .then(response => {
              context.commit(SET_AUTH, response.data)
              resolve(response)
            })
            .catch(err => {
              context.commit(SET_ERROR, err)
              JwtService.destroyAll()
              reject(err)
            })
        })
      })
      .then(() => {
        return new Promise((resolve, reject) => {
          ApiService.get('/admin/permission/info')
            .then(response => {
              context.commit(SET_ROLE_PERMISSION_GROUP, response.data.data)
              resolve(response)
            })
            .catch(err => {
              context.commit(SET_ERROR, err)
              JwtService.destroyAll()
              reject(err)
            })
        })
      })
  },
  [LOGOUT] (context) {
    context.commit(PURGE_AUTH)
    context.commit(PURGE_ROLE_PERMISSION_GROUP)
  },
  [CHECK_AUTH] (context) {
    let user = JwtService.getUser()
    if (JwtService.getToken() && user !== null) {
      ApiService.setHeader()
      context.commit(SET_AUTH, user)
    } else {
      context.commit(PURGE_AUTH)
    }
  },
  [LOGIN_CLIENT] (context, credentials) {
    return new Promise((resolve, reject) => {
      ApiService.post('/member/login', credentials)
        .then(response => {
          const apiToken = response.headers['api-token']
          JwtService.saveToken(apiToken)
          // リフレッシュトークンの取得と保存
          const refreshToken = response.headers['api-refresh-token']
          JwtService.saveRefreshToken(refreshToken)
          ApiService.setHeader()
          context.commit(SET_PASSWORD_REMIND, response.data.passwordRemind)
          resolve(response.data.id)
        })
        .catch(err => {
          context.commit(SET_ERROR_CLIENT, err)
          // JwtService.destroyAll()
          reject(err)
        })
    })
      .then((id) => {
        // GET /member/selfInfo is permitted for every session flag, including the
        // quarantine/terms jails - it is strictly self-scoped, so a member reading their own
        // already-self-provided profile poses no cross-member disclosure risk.
        return new Promise((resolve, reject) => {
          ApiService.get('/member/selfInfo')
            .then(response => {
              context.commit(SET_AUTH_CLIENT, response.data)
              // SET_AUTH_CLIENT has a side effect of reopening the login dialog; close it back
              // here rather than relying on the caller to do so.
              context.commit(OPEN_LOGIN_DIALOG, false)
              refreshProfileAfterUmsSync(context)
              resolve(response)
            })
            .catch(err => {
              context.commit(SET_ERROR_CLIENT, err)
              JwtService.destroyAll()
              // destroyAll() alone leaves the just-saved token attached to the shared axios
              // Authorization default; clear that too.
              ApiService.setHeader()
              reject(err)
            })
        })
      })
  },
  [LOGOUT_CLIENT] (context) {
    // Revokes the Cognito refresh token server-side, best-effort - the local session is
    // always purged regardless of whether this call succeeds.
    return MemberClientApi.logout().catch(() => {}).then(() => {
      context.commit(PURGE_AUTH_CLIENT)
      ApiService.setHeader()
    })
  },
  [CHECK_AUTH_CLIENT] (context) {
    let user = JwtService.getUser()
    if (JwtService.getToken() && user !== null) {
      ApiService.setHeader()
      context.commit(SET_AUTH_CLIENT, user)
      context.commit(OPEN_LOGIN_DIALOG, false)
    } else {
      context.commit(PURGE_AUTH_CLIENT)
      // PURGE_AUTH_CLIENT only clears the cookie/localStorage, not the shared axios
      // Authorization default - clear that too, or a stale token stays attached to every
      // subsequent request.
      ApiService.setHeader()
    }
  },
  [SET_OPEN_LOGIN_DIALOG] (context, payload) {
    if (payload && typeof payload === 'object') {
      context.commit(SET_LOGIN_DIALOG_CONTEXT, payload)
      context.commit(OPEN_LOGIN_DIALOG, true)
      return
    }
    if (payload) {
      context.commit(SET_LOGIN_DIALOG_CONTEXT, null)
    } else {
      context.commit(SET_LOGIN_DIALOG_CONTEXT, null)
    }
    context.commit(OPEN_LOGIN_DIALOG, !!payload)
  },
  [SET_OPEN_REGISTER_DIALOG] (context, isOpenRegisterDialog) {
      context.commit(OPEN_REGISTER_DIALOG, isOpenRegisterDialog)
  }
}

const mutations = {
  [SET_ERROR] (state, error) {
    state.errors = error
  },
  [SET_AUTH] (state, user) {
    state.isAuthenticated = true
    state.user = user
    state.errors = {}
    state.isOpenLoginDialog = true
    state.isOpenRegisterDialog = true
    JwtService.saveUser(user)
  },
  [PURGE_AUTH] (state) {
    state.isAuthenticated = false
    state.user = {}
    state.errors = {}
    state.isOpenLoginDialog = false
    state.isOpenRegisterDialog = false
    state.isPasswordReminded = false
    JwtService.destroyAll()
  },
  [SET_ERROR_CLIENT] (state, error) {
    state.errors = error
  },
  [SET_AUTH_CLIENT] (state, user) {
    state.isAuthenticated = true
    state.user = user
    state.errors = {}
    state.isOpenLoginDialog = true
    state.isOpenRegisterDialog = false
    JwtService.saveUser(user)
  },
  [SET_UPDATE_AUTH_CLIENT] (state, user) {
    state.user = user
    JwtService.saveUser(user)
  },
  [PURGE_AUTH_CLIENT] (state) {
    state.isAuthenticated = false
    state.user = {}
    state.errors = {}
    state.isOpenLoginDialog = false
    state.isOpenRegisterDialog = false
    state.isPasswordReminded = false
    JwtService.destroyAll()
  },
  [OPEN_LOGIN_DIALOG] (state, isOpenLoginDialog) {
    state.isOpenLoginDialog = isOpenLoginDialog
  },
  [SET_LOGIN_DIALOG_CONTEXT] (state, loginDialogContext) {
    state.loginDialogContext = loginDialogContext
  },
  [OPEN_REGISTER_DIALOG] (state, isOpenRegisterDialog) {
      state.isOpenRegisterDialog = isOpenRegisterDialog
  },
  [SET_PASSWORD_REMIND] (state, isPasswordReminded) {
    state.isPasswordReminded = isPasswordReminded
  },
  [SET_ROLE_PERMISSION_GROUP] (state, rolePermissionGroup) {
    state.rolePermissionGroup = rolePermissionGroup
  },
  [PURGE_ROLE_PERMISSION_GROUP]  (state) {
    state.rolePermissionGroup = {}
  }
}

export default {
  state,
  actions,
  mutations,
  getters
}
